/**
 * 下から引き上げる「状況」の引き出し（ADR-0055）の状態。DOM を持たない部分（テストできるように分ける）。
 * 開く・閉じる・指で引いている途中の位置と、指を離したときにどちらへ落ち着くかを決める。
 */

export interface DrawerSettings {
  /** 閉じた状態から、これ以上（px）引き上げたら開く */
  open_px: number;
  /** 開いた状態から、これ以上（px）引き下げたら閉じる */
  close_px: number;
  /** 速さ（px/ms）がこれを超えたら、距離が足りなくても向きに従う */
  fling_px_per_ms: number;
}

export const DEFAULT_DRAWER: DrawerSettings = { open_px: 60, close_px: 80, fling_px_per_ms: 0.5 };

export interface DrawerSnapshot {
  open: boolean;
  /** 引いている途中か */
  dragging: boolean;
  /** 引いている途中の、指の移動（px。上が負）。引いていなければ 0 */
  dy: number;
}

/**
 * 指を離したときに落ち着く側。dy は指の移動（上が負）、v は離した瞬間の速さ（px/ms。下が正）
 */
export function settle(
  open: boolean,
  dy: number,
  v: number,
  s: DrawerSettings = DEFAULT_DRAWER,
): boolean {
  if (Math.abs(v) >= s.fling_px_per_ms) return v < 0;
  if (open) return dy < s.close_px;
  return -dy >= s.open_px;
}

/**
 * 引いている途中の、引き出しの見かけの位置（px。0 = 開いた位置、height = 閉じた位置）。
 * 開いているときは下向きだけ、閉じているときは上向きだけ動く（逆向きに引いても動かない）
 */
export function offsetWhileDragging(open: boolean, dy: number, height: number): number {
  if (open) return Math.min(height, Math.max(0, dy));
  return Math.min(height, Math.max(0, height + dy));
}

export class DrawerState {
  private open_ = false;
  private dragging_ = false;
  private dy_ = 0;
  /** 開いた回数（開くたびに中身を描き直す側が使う） */
  private opens_ = 0;
  private readonly listeners = new Set<(s: DrawerSnapshot) => void>();

  constructor(private readonly settings: DrawerSettings = DEFAULT_DRAWER) {}

  get snapshot(): DrawerSnapshot {
    return { open: this.open_, dragging: this.dragging_, dy: this.dragging_ ? this.dy_ : 0 };
  }
  get open(): boolean {
    return this.open_;
  }
  get opens(): number {
    return this.opens_;
  }

  subscribe(fn: (s: DrawerSnapshot) => void): () => void {
    this.listeners.add(fn);
    fn(this.snapshot);
    return () => this.listeners.delete(fn);
  }
  private emit() {
    const s = this.snapshot;
    for (const fn of this.listeners) fn(s);
  }

  setOpen(v: boolean) {
    const was = this.open_;
    this.open_ = v;
    this.dragging_ = false;
    this.dy_ = 0;
    if (v && !was) this.opens_ += 1;
    this.emit();
  }
  toggle() {
    this.setOpen(!this.open_);
  }

  dragStart() {
    this.dragging_ = true;
    this.dy_ = 0;
    this.emit();
  }
  dragMove(dy: number) {
    if (!this.dragging_) return;
    this.dy_ = dy;
    this.emit();
  }
  /** 指を離した。v は速さ（px/ms。下が正） */
  dragEnd(v = 0) {
    if (!this.dragging_) return;
    const next = settle(this.open_, this.dy_, v, this.settings);
    this.setOpen(next);
  }
  dragCancel() {
    this.dragging_ = false;
    this.dy_ = 0;
    this.emit();
  }
}
